import CloseIcon from "@mui/icons-material/Close";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import {
  Box,
  IconButton,
  List,
  ListItem,
  Modal,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import IconChip from "../../../../../../components/IconChip";
import { Toast } from "../../../../../../components/Toast";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import SearchField from "../../../../../../components/filters/SearchField";
import iconMapping from "../../../../../../icons/IconMapping";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import { csvExportFile } from "../../../../../../scenes/posture/controls/components/EntitiesSection/ExportTable";
import { EntityTypeToDisplayName } from "../../../../../../utils/constants";
import { icons } from "../../../../../stack/environment/icons/iconExport";

export default function AuditLogsModal({ open, onClose, log }) {
  const { trackEvent } = useAPI();
  const colors = useColors();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedEntities, setExpandedEntities] = useState({});
  const [modalWidth, setModalWidth] = useState(500);
  const [modalHeight, setModalHeight] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef({
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
  });

  const toggleEntityExpand = (entityId) => {
    setExpandedEntities((prev) => ({
      ...prev,
      [entityId]: !prev[entityId],
    }));
  };

  // Initialize all entities as expanded by default when log changes
  useEffect(() => {
    if (log?.remediated_entities) {
      const newExpandedState = {};
      log.remediated_entities.forEach((entity) => {
        newExpandedState[entity.entity_id] = true;
      });
      setExpandedEntities(newExpandedState);
    }
  }, [log]);

  // Filtered entities for modal search
  const filteredEntities = log
    ? log.remediated_entities
        .map((remediatedEntity) => ({
          ...remediatedEntity,
          affectedEntities: remediatedEntity.affected_entities.filter(
            (affectedEntity) =>
              affectedEntity.display_name
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
          ),
        }))
        .filter(
          (remediatedEntity) =>
            remediatedEntity.affectedEntities.length > 0 ||
            remediatedEntity.display_name
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
        )
    : [];

  const handleExport = async () => {
    trackEvent(
      `User exported remediated entities for log ${log?.log_id} to CSV`
    );

    if (!log) return;

    try {
      const csvData = [];

      // Add header row
      csvData.push(["Token", "User"]);

      // Add data rows
      if (log.remediated_entities) {
        log.remediated_entities.forEach((entity) => {
          const tokenName = entity.display_name;

          if (entity.affected_entities && entity.affected_entities.length > 0) {
            entity.affected_entities.forEach((user) => {
              csvData.push([tokenName, user.display_name]);
            });
          }
        });
      }

      csvExportFile(csvData, `remediated_entities_${log.remediation_id}`);
    } catch (error) {
      Toast.error("Failed to export to file.");
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: modalWidth,
      startHeight: modalHeight,
    };
  };

  const handleResizeMove = (e) => {
    if (!isResizing) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    const newWidth = Math.max(400, resizeRef.current.startWidth + deltaX);
    const newHeight = Math.max(300, resizeRef.current.startHeight + deltaY);

    setModalWidth(newWidth);
    setModalHeight(newHeight);
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);

      return () => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
      };
    }
  }, [isResizing]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <Paper
          sx={{
            p: 3,
            width: modalWidth,
            height: modalHeight,
            mx: "auto",
            minHeight: 400,
            maxHeight: "90vh",
            borderRadius: 2,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconChip
                icon={
                  <SvgToIcon
                    IconLogo={icons.autoFixIcon}
                    width="20px"
                    style={{ color: colors.icon_action_selected }}
                  />
                }
              />
              <Typography variant="h6">Remediated Entities</Typography>
            </Box>

            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <SearchField
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              colors={colors}
              theme={theme}
            />
            <ExportButton handleExport={handleExport} />
          </Box>

          <Box
            border={1}
            borderColor={colors.divider}
            borderRadius="6px"
            flexGrow={1}
            padding={1}
            sx={{ overflow: "auto" }}
          >
            {filteredEntities.length === 0 ? (
              <Typography variant="body2" color={colors.text_secondary}>
                No entities found.
              </Typography>
            ) : (
              filteredEntities.map((entity) => (
                <Box key={entity.entity_id} mb={1}>
                  <Box
                    display="flex"
                    gap={1}
                    alignItems="center"
                    padding="0 4px"
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleEntityExpand(entity.entity_id)}
                  >
                    <SvgToIcon
                      IconLogo={
                        expandedEntities[entity.entity_id]
                          ? icons.chevronDownIcon
                          : icons.chevronRightIcon
                      }
                      width="20px"
                      style={{ color: colors.icon_action_active }}
                    />
                    <IconChip
                      icon={
                        <SvgToIcon IconLogo={iconMapping[entity.entity_type]} />
                      }
                      tooltip={EntityTypeToDisplayName[entity.entity_type]}
                      bgcolor={colors.entity_chip_enabled_fill}
                      shadowColor={colors.entity_chip_enabled_shadow}
                      boxShadow={colors.entity_chip_enabled_shadow}
                    />
                    <Typography variant="body2">
                      {entity.display_name}
                    </Typography>
                  </Box>
                  {expandedEntities[entity.entity_id] && (
                    <List dense>
                      {entity.affected_entities.map((affectedEntity) => (
                        <ListItem key={affectedEntity.entity_id} sx={{ pl: 2 }}>
                          <Box
                            display={"flex"}
                            gap={1}
                            alignItems={"center"}
                            pl="61px"
                            pt="6px"
                            pb="8px"
                          >
                            <IconChip
                              icon={
                                <SvgToIcon
                                  IconLogo={
                                    iconMapping[affectedEntity.entity_type]
                                  }
                                />
                              }
                              tooltip={
                                EntityTypeToDisplayName[
                                  affectedEntity.entity_type
                                ]
                              }
                              bgcolor={colors.entity_chip_enabled_fill}
                              shadowColor={colors.entity_chip_enabled_shadow}
                              boxShadow={`0px 0px 2px 0px ${colors.misconfig_chip_enabled_shadow}`}
                            />
                            <Typography
                              variant="body2"
                              color={colors.text_secondary}
                            >
                              {affectedEntity.display_name}
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))
            )}
          </Box>

          {/* Resize Handle */}
          <IconButton
            onMouseDown={handleResizeStart}
            aria-label="Resize modal"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              cursor: "se-resize",
              "&:hover": {
                backgroundColor: colors.divider,
              },
            }}
            size="small"
          >
            <OpenWithIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>
    </Modal>
  );
}
